import React from "react";
import { withRouter } from "react-router-dom";
import { flowRight as compose } from 'lodash';
import { withStore } from "../../store";
import SearchFilters from "../../components/SearchFilters/SearchFilters";
import Grid from "../../components/Grid/Grid";
import Loading from "../../components/Loading/Loading";
import { childrenTileMapper, fetchHandler, getClientApiURL } from "../../utils";
import _ from "lodash";
import "./VideoAndAudioCatalog.scss";

class VideoAndAudioCatalog extends React.Component<any, any> {

  constructor(props) {
    super(props);
    this.state = {
      filters: {},
      initialCatalog: undefined,
      catalog: undefined,
      loading: true,
      performances: {}
    }
  }

  componentDidMount() {
    this.fetchFiltersData();
  }

  onSearch = (filters, selectedFilter, reloadResults = true, reloadCategory = false) => {
    this.setState({
      filters,
      loading: reloadResults,
      categoryLoading: reloadCategory,
    });
    this.fetchFiltersData(undefined, filters, selectedFilter);
  }

  hasFilters = () => {
    const { filters } = this.state;
    return Object.values(filters).length > 0;
  }

  getFilterUrl = (filters) => {
    let url = `${getClientApiURL("/filter")}`;
    if (filters) {
      url = `${url}?`;
      for (const key in filters) {
        if (filters[key])
          url = url.concat(`&${key}=${filters[key]}`);
      }
    }
    return url;
  }

  // MET2-2898: the per-opera tile fetch (grid expansion) must carry the active
  // filters too. Without them the backend `/filter/:operaName` route returns
  // every performance for that opera regardless of media_type, so expanding a
  // grid while "Audio Only" is selected surfaced HD/SD video tiles. The route
  // already scopes by opera via the path param, so we drop `opera` here and
  // forward the rest (media_type / composer / artist). Mirrors getFilterUrl's
  // `?` + `&key=value` convention: getClientApiURL may already append a
  // proxy query param (origin_hostname) in local dev, so each filter must be
  // joined with `&` to stay a real query parameter.
  getPerformanceUrl = (operaName, filters) => {
    let url = `${getClientApiURL(`/filter/${encodeURIComponent(operaName.toLowerCase())}`)}`;
    if (filters) {
      url = `${url}?`;
      for (const key in filters) {
        if (key !== "opera" && filters[key])
          url = url.concat(`&${key}=${filters[key]}`);
      }
    }
    return url;
  }

  // MET2-2898: stable signature of the active filter *values* (not which
  // panel is open). Used to key the per-opera tile cache and to remount the
  // grids so a filter change can't show tiles fetched under a previous filter.
  getFiltersSignature = (filters) => {
    if (!filters) return "";
    return Object.keys(filters)
      .filter((key) => filters[key])
      .sort()
      .map((key) => `${key}=${filters[key]}`)
      .join("&");
  }

  getPerformanceCacheKey = (operaName, filters) => `${this.getFiltersSignature(filters)}::${operaName}`;

  fetchFiltersData = (operaName, filters, selectedFilter)  => {
    const { performances } = this.state;
    let url;
    if (operaName) {
      url = this.getPerformanceUrl(operaName, filters);
    } else {
      url = this.getFilterUrl(filters);
    }
    if (selectedFilter)
      url = url.concat(`&active=${selectedFilter}`);
    const operaCacheKey = operaName ? this.getPerformanceCacheKey(operaName, filters) : null;

    // MET2-2898: guard against out-of-order responses. Opening a filter
    // category and then selecting a value fire two catalog requests in quick
    // succession; the first (broader, e.g. media_type=audio) and the second
    // (narrowed, e.g. media_type=audio&opera=Aida) both setState `catalog`,
    // so if the broader one resolves last it clobbers the narrowed results and
    // the UI shows everything instead of the intersection. Tag each catalog
    // request and apply only the most recent one's response.
    const requestId = operaName ? null : (this.catalogRequestSeq = (this.catalogRequestSeq || 0) + 1);

    fetchHandler(url, {}, (response) => {
      if (operaName) {
        performances[operaCacheKey] = response;
        this.setState({
          performances,
          loading: false,
          categoryLoading: false,
        });
        return;
      }
      // Ignore stale catalog responses that arrive after a newer request.
      if (requestId !== this.catalogRequestSeq) return;
      const update: any = {
        catalog: response,
        loading: false,
        categoryLoading: false,
      };
      if (!this.state.initialCatalog) update.initialCatalog = response;
      this.setState(update);
    });
  }

  onExpandGrid = (operaName, gridExpanded) => {
    const { performances, filters } = this.state;
    const cacheKey = this.getPerformanceCacheKey(operaName, filters);
    if (!gridExpanded && operaName && !performances[cacheKey]) {
      // MET2-2898: forward the active filters so expanded tiles respect the
      // current media_type / composer / artist selection.
      this.fetchFiltersData(operaName, filters);
    }
  }

  getPerformancesGridInfo = (result) => {
    const { performances, filters } = this.state;
    const cacheKey = this.getPerformanceCacheKey(result.name, filters);
    const performancesGrid = {
      header: result.name,
      videoCount: result.videoCount,
      audioCount: result.audioCount,
      tiles: !_.isEmpty(performances) && performances[cacheKey] ? childrenTileMapper(performances[cacheKey]) : []
    }
    return performancesGrid;
  }

  render() {
    const { loading, catalog, initialCatalog, categoryLoading, filters } = this.state;
    const { header } = this.props;
    // MET2-2898: include the active-filter signature in each grid's key so a
    // filter change remounts (and collapses) the grids. Otherwise a grid left
    // expanded under one filter would keep rendering tiles fetched under it.
    const filtersSignature = this.getFiltersSignature(filters);

    return (
      <div className="VideoAndAudioCatalog" id="VideoAndAudioCatalog">
      <h2 className="VideoAndAudioCatalog-header">{header}</h2>
      <SearchFilters catalog={initialCatalog} filters={catalog && catalog.filters} categoryLoading={categoryLoading} onSearch={this.onSearch} />
      { loading && <Loading inline={true} label="Loading..." centered="true" /> }
      <div className="VideoAndAudioCatalog-filter-results">
        { this.hasFilters() &&
          <h3>Filtered Results</h3>
        }
        {
          catalog && catalog.results.map((result, index) => {
            const performancesGridInfo = this.getPerformancesGridInfo(result);
            return <Grid key={`${filtersSignature}-${index}`} {...performancesGridInfo} expandGrid={false} onExpandGrid={this.onExpandGrid} />
          })
        }
      </div>
      </div>
    )
  }
};

export default compose(withRouter, withStore)(VideoAndAudioCatalog);
